<template>
  <!-- Navigation Bar -->
  <div
    class="shrink-0 relative z-[1001] lg:!z-[1000] bg-primary-blue-hover"
    :class="[isHeaderIniting && 'header-initing', isGuest && 'history-hidden']"
  >
    <PtAIHistoryNav
      v-model="opened"
      :menus="navMenuList as any"
      :historyMenu="navHistory as any"
      :messages="historyPreTitleMessages"
      responsive
      @clickHistory="viewResult"
      @removeHistory="onRemoveHistory"
    >
      <!-- pc端logo -->
      <template #productLogo="{ opened }">
        <div
          v-if="isHeaderIniting"
          class="common-nav-wide-logo w-full py-5 relative min-h-[72px] flex items-center justify-center"
        >
          <div class="bg-transparent w-[196px] h-[32px]"></div>
        </div>
        <a
          v-else-if="headerService.whiteLabel && headerService.customLogoVisible"
          class="common-nav-wide-logo w-full py-5 relative custom-logo min-h-[72px]"
          :href="headerService.logoHref || '/home'"
        >
          <span class="px-0 flex justify-center">
            <img
              v-if="opened"
              :src="isDark ? headerService.customWhiteLogoUrl : headerService.customLogoUrl"
              alt="Custom Logo"
              class="custom-logo-img"
            />
            <img
              v-else
              :src="headerService.customLogoUrl"
              alt="Custom Logo"
              class="w-6 h-6 object-contain"
            />
          </span>
        </a>
        <a v-else :href="isGuest ? '/' : '/home'" class="common-nav-wide-logo w-full py-5 relative">
          <span class="px-0 flex" :class="{ 'justify-center': !opened, 'justify-start': opened }">
            <PtIcon v-if="opened" :icon="Logo" class="!w-[100px] !h-[30px]" />
            <PtIcon v-else :icon="MiniLogo" class="!w-6 !h-6" />
          </span>
        </a>
      </template>

      <!-- 移动端logo -->
      <template #logo>
        <a
          v-if="headerService.whiteLabel && headerService.customLogoVisible"
          :href="headerService.logoHref || '/home'"
        >
          <img
            :src="headerService.customLogoUrl"
            alt="Custom Logo"
            class="custom-logo-img mobile"
          />
        </a>
        <a v-else :href="isGuest ? '/' : '/home'">
          <PtIcon :icon="Logo" class="!w-[100px] !h-[30px]" />
        </a>
      </template>
      <template #avatar="{ opened }">
        <div
          class="eureka-nav-bar-avatar sticky bottom-0 bg-primary-blue-hover mx-3 cursor-default mt-auto border-solid border-white/20 flex flex-row items-center gap-4 lg:border-t lg:flex-col lg:py-4"
          :class="{ 'eureka-nav-bar-avatar-jp': locale === 'jp' }"
        >
          <Incognito
            v-if="incognitoVisible"
            class="w-full"
            :opened="opened"
            :menu="incognitoItem"
            @handleClick="removeAllHistory"
          />
          <div
            v-if="!isLogin && opened && showLoginTip"
            class="hidden absolute bottom-[80px] left-0 lg:block w-[196px] p-3 bg-white text-grey-450 rounded-xl shadow-lg"
          >
            <img
              :src="signUpBg"
              alt="Sign Up Background"
              class="absolute z-1 top-0 left-0 w-full rounded-lg"
            />
            <div class="mb-3 relative z-10 flex justify-between items-center">
              <div class="font-semibold text-base">{{ t('GuestToMaster.subject') }}</div>
              <PtIcon
                :icon="iconClose"
                @click="showLoginTip = false"
                class="!w-6 !h-6 fill-grey-450 cursor-pointer"
              />
            </div>
            {{ t('GuestToMaster.rights') }}
            <div class="absolute -bottom-[9px] left-[15px]}">
              <ArrowDown />
            </div>
          </div>
          <template v-if="showUpgrade">
            <ElTooltip
              :disabled="opened"
              effect="dark"
              :content="t('NavBar.newUpgrade')"
              placement="right"
              popper-class="!bg-grey-450 !border-0"
            >
              <button
                class="flex items-center justify-center gap-1 text-xs py-1 px-1 bg-blue-40 hover:bg-blue-75 text-eureka-500 rounded-lg"
                @click="handleUpgrade"
                :class="[opened ? 'w-[196px]' : 'w-fit']"
              >
                <PtIcon :icon="UpgradeIcon" class="th-btn-icon text-2xl" />
                <span v-if="opened">{{ t('NavBar.newUpgrade') }}</span>
              </button>
            </ElTooltip>
          </template>
          <PtNavUserAvatar :open="opened" :service="headerService" @clickLogin="handleLogin">
            <template #userPackageType>
              <UserPackageType />
            </template>
          </PtNavUserAvatar>
        </div>
      </template>
    </PtAIHistoryNav>
    <MobileTag :isLogin="isLogin" v-if="!isInFeiShuClient && isCompany" />

    <!-- Search Task Dialog -->
    <SearchTaskDialog
      :isTaskLoading="isTaskLoading"
      ref="searchTaskDialogRef"
      :tasks="sessionsTaskData"
      @task-click="handleSearchTaskClick"
      @new-task="handleNewSearchTask"
      @search="handleSearchTask"
    />
  </div>
</template>

<script setup lang="ts">
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import type { IPackageType } from '@eureka/services/src/apis/common.api.types';
import MobileTag from '@eureka/shared/edition-upgrade/src/components/edition-tag/MobileTag.vue';
import { UserPackageType } from '@eureka/shared/header-menu-3';
import { loginSDK } from '@eureka/shared/login-sdk';
import { usePlg } from '@eureka/shared/plg-plan/src/composables/use-plg';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { clientDeferred } from '@eureka/utils/client';

import iconClose from '@patsnap-ui/icon/assets/solid/CloseMedium.svg';
import '@patsnap-ui/icon/assets/solid/InsightsTechnology.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/NavWorkspace.svg';
import '@patsnap-ui/icon/assets/solid/ChartTopipc.svg';
import '@patsnap-ui/icon/assets/solid/ChartRing.svg';
import '@patsnap-ui/icon/assets/solid/robot.svg';
import '@patsnap-ui/icon/assets/solid/info.svg';

import { config, type HeaderService, type ListItem, Site, SLD } from '@patsnap-utils/biz';
import { injectService } from '@patsnap/tahiti-core';
import type { T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import { useDark } from '@vueuse/core';
import { ElTooltip } from 'element-plus';
import _ from 'lodash';
import { PtAIHistoryNav, PtIcon, PtNavUserAvatar } from 'patsnap-biz';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import UpgradeIcon from '../../edition-upgrade/src/assets/icon/upgrade.svg';
import { usePlgPlanSdk } from '../../plg-plan/src/composables/use-plg-plan-sdk';
import Logo from './assets/logo.svg';
import MiniLogo from './assets/mini-logo.svg';
import signUpBg from './assets/sign-up-bg.png';
import ArrowDown from './components/icons/ArrowDown.vue';
import Incognito from './components/Incognito.vue';
import SearchTaskDialog from './components/SearchTaskDialog.vue';
import { useNavBar } from './composables/use-nav-bar';
import { useNavBarState } from './composables/use-navbar-store';
import { INCOGNITO_MENU_ITEM, ROUTE_NAME_WHITELIST } from './constants';
import { cn, en, jp, tw } from './infrastructure/locales';
import type { HistoryRowData, NAV_MENU_TYPE } from './types';
import '@patsnap-ui/icon/assets/solid/FolderMany.svg';
import { isOverseaOrStage } from '../../plg-plan/src/constants';

const { t, locale } = useI18n({
  messages: {
    cn,
    en,
    jp,
    tw,
  },
});

const route = useRoute();
const router = useRouter();

const emits = defineEmits(['handleUpgrade']);

const handleUpgrade = async () => {
  emits('handleUpgrade');
};

const props = defineProps<{
  headerService: HeaderService;
  login?: () => void;
  navModuleType: NAV_MENU_TYPE;
}>();

const biTrackService = injectService<BiTrackService>(BiTrackService);

const isDark = useDark();

const {
  opened,
  currentDetailId,
  routeName,
  shouldSave,
  currentRouteFlag,
  updateNavStatus,
  save,
  getSessionsTaskData,
  sessionsTaskData,
  isTaskLoading,
} = useNavBarState();

const searchTaskDialogRef = ref<InstanceType<typeof SearchTaskDialog> | null>(null);

const {
  historyPreTitleMessages,
  viewResult,
  onRemoveHistory,
  removeAllHistory,
  navMenuList,
  navHistory,
} = useNavBar(props as any, searchTaskDialogRef);

const { isFreemium, isCompany } = usePlg();

const { openPlgPlanDialog } = usePlgPlanSdk({ router, route });

const isLogin = computed(() => {
  return props.headerService.userCenterService.logged;
});
const userPackages = ref<IPackageType[]>([]);

props.headerService.userCenterService.beforeLogoutHandlers.add(async function () {
  if (!['cn'].includes(process.env.VUE_APP_DEPLOY_ENV as T_Region)) {
    await import(/* webpackChunkName: "gis" */ './infrastructure/utils/gis')
      .then(({ loadGis, disableAutoSelect }) => {
        return loadGis().then(disableAutoSelect);
      })
      .catch((e) => {
        console.error('[gis] load gis failed', e);
      });
  }
  const service = props.headerService.userCenterService;
  service.logged = false;
  service.setUserInfo();
  localStorage.removeItem('patsnap-authorizations');
  const { href } = new Site({ sld: SLD.PASSPORT, pathname: 'logout' });
  const guestPage = `${location.origin}/home`;
  location.href = `${href}?from=${config.clientName}&redirect_uri=${guestPage}`;
  throw new Error('Logout in NavBar');
});

const isPersonal = ref(false);
const showLoginTip = ref(true);
const isGuest = ref(false);

const isInFeiShuClient = ref(false);

const isHeaderIniting = ref(!!props.headerService.deferred.promise);

props.headerService.deferred.promise.then(() => {
  isHeaderIniting.value = false;
});

const showUpgrade = computed(() => {
  return !isInFeiShuClient.value && isFreemium.value && isOverseaOrStage;
});

const incognitoItem = computed(() => {
  return {
    ...INCOGNITO_MENU_ITEM,
    title: t(`NavBar.${INCOGNITO_MENU_ITEM.type}`),
    tip: t(`NavBar.${INCOGNITO_MENU_ITEM.type}_TIP`),
  };
});

const incognitoVisible = computed(() => {
  return _.get(props.headerService.account, '_info.incognito_flag');
});

onMounted(async () => {
  const account = await accountDeferred.promise;
  const buildin = props.headerService.userCenterService.beforeRenderMenuItems;
  clientDeferred.promise.then((res) => {
    isInFeiShuClient.value = res.isFeiShu;
  });
  props.headerService.userCenterService.beforeRenderMenuItems = function (
    items: ListItem[],
  ): ListItem[] {
    isPersonal.value = !account?._info?.company_id || account?._info?.company_id === undefined;
    props.headerService.visibleProducts = !isPersonal.value && !isGuest.value;
    const _items = buildin(items);

    if (isPersonal.value) {
      const nonePackages = ['package', 'settings', 'team'];
      return _items.filter((item) => !nonePackages.includes(item.key as string));
    }
    return _items;
  };

  CommonApis.getUserPackageType().then((res) => {
    userPackages.value = res;
  });
});

watch(
  () => currentRouteFlag.value,
  () => {
    if (_.includes(ROUTE_NAME_WHITELIST, routeName.value) || !currentDetailId.value) return;
    updateNavStatus(false, false);
  },
  { immediate: true },
);

watch(
  () => opened.value,
  (value: boolean) => {
    if (shouldSave.value) {
      save(value as boolean);
    }
    // 如果当前路由是credits页面，则更新creditsNumber
  },
);

function handleLogin() {
  if (props.login) {
    props.login();
    return;
  }
  loginSDK.open().then(() => {
    window.location.reload();
  });
}

function handleSearchTaskClick(task: HistoryRowData) {
  // 兼容ls
  if (task?.rawType.startsWith('ls') && !task.link.startsWith('/ls/#')) {
    window.location.href = `/ls/#${task.link}`;
  } else {
    window.location.href = task.link;
  }
}

function handleSearchTask(query: string) {
  getSessionsTaskData(query);
}

function handleNewSearchTask() {
  const guestPage = `${location.origin}/home`;
  location.href = guestPage;
}

onMounted(async () => {
  isGuest.value = !(await isUserLogin());
  userPackages.value = await CommonApis.getUserPackageType();
});
</script>

<style lang="scss">
// 游客版隐藏历史记录
.history-hidden {
  .patsnap-biz-ai-history-nav-wide
    .ai-history-nav__smooth-scroll
    .patsnap-biz-ai-history-item:last-child {
    @apply hidden;
  }
  .patsnap-biz-ai-history-nav-collapse
    .ai-history-nav__smooth-scroll
    > .patsnap-biz-ai-history-item {
    @apply hidden;
  }
}
.eureka-nav-bar-avatar-jp {
  .patsnap-biz-nav-user_center--login {
    @apply px-5;
  }
}
.patsnap-biz-ai-history-nav .menu-divide-bottom-line::after {
  display: none;
}
.common-nav-wide-logo {
  &::after {
    display: none;
    content: '';
    @apply absolute left-3 right-3 bottom-0 h-px bg-white/20;
  }

  &.custom-logo {
    .custom-logo-img {
      max-height: 48px;
      max-width: 100%;
      height: auto;
      width: auto;
      @apply transition-all duration-200;

      &.mobile {
        max-height: 24px !important;
        width: auto;
        height: 24px !important;
        object-fit: contain;
        @apply transition-all duration-200;
      }
    }
  }
}
.nav-history-sub-item {
  &.active-menu-item,
  &.tippy-content-active {
    span:last-child {
      display: none !important;
    }
  }
}

// 添加移动端特定样式
.patsnap-biz-mobile-nav-bar {
  .custom-logo-img.mobile {
    max-height: 24px !important;
    height: 24px !important;
    width: auto;
    object-fit: contain;
  }
}

.header-initing {
  .patsnap-biz-nav-header,
  .eureka-nav-bar-avatar {
    display: none;
  }
}
.nav-bar-credits-entry {
  .pt-user-credits {
    padding: 10px 0 0 0;
  }
}
</style>
